import React from "react";
import {
    ArrowLeft,
    LockKeyhole,
    Sparkles,
    UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const ChildLogin = () => {
    const navigate = useNavigate();

    const [username, setUsername] =
        React.useState("");

    const [password, setPassword] =
        React.useState("");

    const [checkingSession, setCheckingSession] =
        React.useState(true);

    const [submitting, setSubmitting] =
        React.useState(false);

    const [errorMessage, setErrorMessage] =
        React.useState("");

    React.useEffect(() => {
        let cancelled = false;

        const checkChildSession = async () => {
            try {
                const response = await api.get(
                    "/auth/child/me"
                );

                if (cancelled) return;

                const childId =
                    response.data?.child_id;

                if (childId) {
                    navigate(
                        `/child/home/${childId}`,
                        {
                            replace: true,
                        }
                    );

                    return;
                }
            } catch (error) {
                // No active child session.
            }

            if (!cancelled) {
                setCheckingSession(false);
            }
        };

        checkChildSession();

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        setErrorMessage("");
        setSubmitting(true);

        try {
            const response = await api.post(
                "/auth/child/login",
                {
                    username: username.trim(),
                    password,
                }
            );

            const childId =
                response.data?.child?.child_id;

            if (!childId) {
                throw new Error(
                    "Child profile was not returned."
                );
            }

            navigate(
            `/child/home/${childId}`,
            {
                replace: true,
            }
        );
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.detail ||
                    "Unable to sign in. Check the username and password."
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (checkingSession) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#05070d] text-[#fff3c4]">
                <p className="animate-pulse text-sm">
                    Opening the Academy…
                </p>
            </div>
        );
    }

    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-[#05070d]
                text-white
                bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.18),transparent_42%)]
            "
        >
            <div className="absolute left-1/2 top-[-180px] h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[#7c3aed]/10 blur-3xl" />

            <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/parent/login")
                        }
                        className="
                            mb-5
                            inline-flex
                            items-center
                            gap-2
                            rounded-2xl
                            border
                            border-[#ffd166]/35
                            bg-black/70
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-[#fff3c4]
                            transition
                            hover:border-[#ffd166]
                            hover:text-[#ffd166]
                        "
                    >
                        <ArrowLeft size={16} />
                        Parent Sign In
                    </button>

                    <section
                        className="
                            overflow-hidden
                            rounded-[32px]
                            border
                            border-[#ffd166]/45
                            bg-black
                            shadow-[0_0_45px_rgba(255,209,102,0.12)]
                        "
                    >
                        <header className="border-b border-[#ffd166]/20 px-7 py-7 text-center">
                            <div
                                className="
                                    mx-auto
                                    flex
                                    h-16
                                    w-16
                                    items-center
                                    justify-center
                                    rounded-full
                                    border
                                    border-[#ffd166]/55
                                    bg-[#2b1548]/70
                                    text-[#ffd166]
                                    shadow-[0_0_24px_rgba(168,85,247,0.35)]
                                "
                            >
                                <Sparkles size={28} />
                            </div>

                            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.28em] text-[#d8b4fe]">
                                Liberi Curiosi
                            </p>

                            <h1 className="mt-2 font-display text-4xl text-[#ffd166]">
                                Enter the Academy
                            </h1>

                            <p className="mt-3 text-sm leading-relaxed text-[#b0b8d6]">
                                Sign in with the username
                                and password created by your
                                parent.
                            </p>
                        </header>

                        <form
                            onSubmit={handleSubmit}
                            className="space-y-5 px-7 py-7"
                        >
                            <label className="block">
                                <span className="mb-2 block text-sm font-semibold text-[#fff3c4]">
                                    Username
                                </span>

                                <div className="relative">
                                    <UserRound
                                        size={18}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d8b4fe]"
                                    />

                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(event) =>
                                            setUsername(
                                                event.target
                                                    .value
                                            )
                                        }
                                        autoComplete="username"
                                        required
                                        minLength={3}
                                        maxLength={32}
                                        className="
                                            w-full
                                            rounded-2xl
                                            border
                                            border-[#ffd166]/35
                                            bg-[#080b14]
                                            py-3
                                            pl-12
                                            pr-4
                                            text-[#fff3c4]
                                            outline-none
                                            transition
                                            placeholder:text-[#737b99]
                                            focus:border-[#ffd166]
                                            focus:shadow-[0_0_18px_rgba(255,209,102,0.14)]
                                        "
                                        placeholder="Your username"
                                    />
                                </div>
                            </label>

                            <label className="block">
                                <span className="mb-2 block text-sm font-semibold text-[#fff3c4]">
                                    Password
                                </span>

                                <div className="relative">
                                    <LockKeyhole
                                        size={18}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#d8b4fe]"
                                    />

                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(event) =>
                                            setPassword(
                                                event.target
                                                    .value
                                            )
                                        }
                                        autoComplete="current-password"
                                        required
                                        minLength={6}
                                        maxLength={128}
                                        className="
                                            w-full
                                            rounded-2xl
                                            border
                                            border-[#ffd166]/35
                                            bg-[#080b14]
                                            py-3
                                            pl-12
                                            pr-4
                                            text-[#fff3c4]
                                            outline-none
                                            transition
                                            placeholder:text-[#737b99]
                                            focus:border-[#ffd166]
                                            focus:shadow-[0_0_18px_rgba(255,209,102,0.14)]
                                        "
                                        placeholder="Your password"
                                    />
                                </div>
                            </label>

                            {errorMessage && (
                                <div className="rounded-2xl border border-red-400/35 bg-red-950/35 px-4 py-3 text-sm text-red-200">
                                    {errorMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={
                                    submitting ||
                                    !username.trim() ||
                                    password.length < 6
                                }
                                className="
                                    inline-flex
                                    w-full
                                    items-center
                                    justify-center
                                    gap-2
                                    rounded-2xl
                                    border
                                    border-[#ffd166]/55
                                    bg-[#2b1548]/80
                                    px-5
                                    py-3
                                    font-semibold
                                    text-[#fff3c4]
                                    shadow-[0_0_22px_rgba(168,85,247,0.28)]
                                    transition
                                    hover:border-[#ffd166]
                                    hover:bg-[#3b1d62]
                                    hover:text-[#ffd166]
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                "
                            >
                                <Sparkles size={18} />

                                {submitting
                                    ? "Entering…"
                                    : "Enter the Academy"}
                            </button>
                        </form>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ChildLogin;